import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import {
  hideSidePanel,
} from '../../store/actions/sidePanelActions';
import AddTaskComponent from '../Tasks/AddTaskComponent';
import { SidePanelState } from '../../store/reducer/sidePanelReducer';
import TimekeepingSidePanel from '../TimeTracker/SidePanel';
import Onboarding from '../Management/Employees/Onboarding';
import FileReaderComponent from './FileReader';
import TimeEntryDetailsSidePanel from '../TimeTracker/TimeEntryDetailsSidePanel';
import EmployeeCaseDetailPanel from '../Management/Employees/EmployeeCaseDetailPanel';
import EmployeeTaskDetailPanel from '../Management/Employees/EmployeeTaskDetailPanel';
import ExtractedContentSidePanel from './ExtractedContentSidePanel';
import TransactionDetails from './Subscriptions/TransactionDetails';
import CustomFileReader from './CustomFileReader';
import DraftTypeModal from './Drafts/DraftTypeModal';
import HelpContent from '../Help/Manual/HelpContent';
import DraftTypeFilesSidePanel from './Drafts/DraftTypeFilesSidePanel';
import ReaderComponent from './TailwindComponents/ReaderComponent';
import DraftSettingsSidePanel from './Drafts/DraftSettingsSidePanel';
import DocumentCustomizationPanel from './Drafts/DocumentCustomizationPanel';
import QuestionnairePanel from './QuestionnairePanel';
import CollaboratorsPanel from './CollaboratorPanel';
import LicenseDetailPanel from './Licenses/LicenseDetailPanel';
import AssignLicensePanel from '../Admin/AssignLicensePanel';
import ProjectResourcesPanel from '../Projects/ProjectResourcesPanel';
import AddClientToMatterModal from '../Matters/AddClientToMatterModal';
import ResourceCollaboratorsSidePanel from './ResourceCollaboratorsSidePanel';
import FileUploader from './TailwindComponents/FileUploader';
import AddSourceModalContent from './AddSourceModalContent';
import { PiXLight } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import ClientForm from '../Clients/ClientForm';
import AddTableDocumentPanel from '../BatchTables/AddTableDocumentPanel';
import SaveTableTemplatePanel from '../BatchTables/SaveTableTemplatePanel';
import ClauseRuleFormPanel from '../Review/ClauseRuleFormPanel';
import GlobalRuleFormPanel from '../Review/GlobalRuleFormPanel';

// Self-contained upload panel with file state management
const UploadAttachmentPanel: React.FC<{ onConfirm?: (data: any) => void; onClose: () => void }> = ({ onConfirm, onClose }) => {
  const [files, setFiles] = React.useState<File[]>([]);
  const { t } = useTranslation();
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-5">
        <FileUploader files={files} onFilesChange={setFiles} />
      </div>
      <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-4 flex justify-end gap-2">
        <Button variant="neutral" size="small" onClick={onClose}>{t('Cancel')}</Button>
        <Button variant="primary" size="small" onClick={() => { if (onConfirm) onConfirm({ files }); onClose(); }} disabled={files.length === 0}>
          {t('Upload')}
        </Button>
      </div>
    </div>
  );
};

const GlobalSidePanel: React.FC = () => {
  const sidePanelState = useSelector(
    (state: RootState): SidePanelState => state.sidePanel
  );
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const contentRef = useRef<any>(null);

  const handleClose = () => {
    if (sidePanelState.onCancel) {
      sidePanelState.onCancel();
    }
    dispatch(hideSidePanel());
  };

  const renderContent = () => {
    switch (sidePanelState.type) {
      case 'addTask':
        return <AddTaskComponent />;
      case 'helpContent':
        return <HelpContent content={sidePanelState.content} />;
      case 'onboarding':
        return (
          <Onboarding
            onClose={handleClose}
          />
        );
      case 'addClient':
        return (
          <ClientForm
            onCancel={handleClose}
            hideHeader={true}
            onClientCreated={(client) => {
              if (sidePanelState.onClientCreated) {
                sidePanelState.onClientCreated(client);
              }
              handleClose();
            }}
          />
        )
      case 'timekeepingView':
        return (
          <TimekeepingSidePanel
            day={sidePanelState.day}
            data={sidePanelState.data}
            isLoading={sidePanelState.isLoading}
            stats={sidePanelState.stats}
            onClose={handleClose}
          />
        );
      case 'createProject':
        return <></>;

      // case 'addTeam':
      //   return <AddTeamForm onClose={handleClose} />;


      case 'fileReader':
        return (
          <FileReaderComponent
            fileUrl={sidePanelState.fileUrl}
            fileType={sidePanelState.fileType}
          />
        )
      case 'reader':
        return (
          <ReaderComponent
            citation={sidePanelState.citation}
            matterId={sidePanelState.matterId}
            sourceType={sidePanelState.sourceType as 'file' | 'law' | 'case' | 'document'}
            userAccessLevel={sidePanelState.userAccessLevel}
            onDelete={sidePanelState.onDelete}
          />
        );
      case 'fileReaderTst':
        return (
          <CustomFileReader
            fileUrl={sidePanelState.fileUrl}
            content={sidePanelState.content}
            isPdf={sidePanelState.isPdf}
            filename={sidePanelState.filename || 'Document'}
          />
        );
      case 'timeEntryDetails':
        return (
          <TimeEntryDetailsSidePanel
            entryData={sidePanelState.data}
            onClose={handleClose}
          />
        );

      case 'employeeTaskDetail':
        return <EmployeeTaskDetailPanel taskData={sidePanelState.taskData} />;

      case 'employeeCaseDetail':
        return <EmployeeCaseDetailPanel caseData={sidePanelState.caseData} />;
      case 'extractedContent':
        return (
          <ExtractedContentSidePanel
            filename={sidePanelState.filename}
            content={sidePanelState.content}
          />
        );
      case 'transactionDetails':
        return (
          <TransactionDetails transactionId={sidePanelState.transaction_id} />

        );
      case 'draftTypes':
        return (
          <DraftTypeModal
            caseId={sidePanelState.caseId}
            onClose={handleClose}
            onSave={sidePanelState.onSave!}
          />
        );
      case 'draftTypeFiles':
        return <DraftTypeFilesSidePanel ref={contentRef} caseId={sidePanelState.caseId} draftTypeName={sidePanelState.draftTypeName} />;
      case 'draftSettings':
        return <DraftSettingsSidePanel matterId={sidePanelState.matterId} draftId={sidePanelState.draftId} onClose={handleClose} />;
      case 'questionnairePanel':
        return (
          <QuestionnairePanel
            ref={contentRef}
            matterId={sidePanelState.matterId}
            questionIndex={sidePanelState.questionIndex}
            item={sidePanelState.item as any}
            onClose={handleClose}
            onUpdate={sidePanelState.onUpdate}
          />
        );
      case 'collaboratorsPanel':
        return (
          <CollaboratorsPanel
            ref={contentRef}
            caseId={sidePanelState.caseId}
            summary={sidePanelState.summary}
            onClose={handleClose}
            onUpdate={sidePanelState.onUpdate}
          />
        );
      case 'documentCustomization':
        return (
          <DocumentCustomizationPanel
            matterUuid={sidePanelState.matterUuid}
            documentTypeId={sidePanelState.documentTypeId}
            documentType={sidePanelState.documentType}
            documentName={sidePanelState.documentName}
            onClose={handleClose}
          />
        );
      case 'licenseDetail':
        return (
          <LicenseDetailPanel
            plan={sidePanelState.plan}
            billingPeriod={sidePanelState.billingPeriod}
            isCurrentPlan={sidePanelState.isCurrentPlan}
            isActive={sidePanelState.isActive}
            currentPlanSortOrder={sidePanelState.currentPlanSortOrder}
            seatCount={sidePanelState.seatCount}
            seatsUsed={sidePanelState.seatsUsed}
            onUpdateSeats={sidePanelState.onUpdateSeats}
            onAction={(p) => {
              if (sidePanelState.onAction) sidePanelState.onAction(p);
              handleClose();
            }}
          />
        );
      case 'assignLicense':
        return (
          <AssignLicensePanel
            userIds={sidePanelState.userIds}
            userNames={sidePanelState.userNames}
            onClose={handleClose}
            onComplete={sidePanelState.onComplete}
          />
        );
      case 'projectResources':
        return (
          <ProjectResourcesPanel
            projectUuid={sidePanelState.projectUuid}
            onClose={handleClose}
            onResourceLinked={sidePanelState.onResourceLinked}
          />
        );
      case 'addClientToMatter':
        return (
          <AddClientToMatterModal
            matterUuid={sidePanelState.matterUuid || ''}
            clientRoleOptions={sidePanelState.clientRoleOptions || []}
            existingMatterClients={(sidePanelState as any).existingMatterClients || []}
            onClientAdded={async () => {
              if (sidePanelState.onRefresh) await sidePanelState.onRefresh();
              if (sidePanelState.onAfterSave) await sidePanelState.onAfterSave();
            }}
          />
        );
      case 'uploadAttachment':
        return (
          <UploadAttachmentPanel
            onConfirm={sidePanelState.onConfirm}
            onClose={handleClose}
          />
        );
      case 'addSource':
        return (
          <AddSourceModalContent
            sourceType={(sidePanelState as any).sourceType || 'law'}
            onConfirm={(sourceData: any) => {
              if (sidePanelState.onConfirm) sidePanelState.onConfirm(sourceData);
              handleClose();
            }}
            onCancel={handleClose}
          />
        );
      case 'addTableDocument':
        return (
          <AddTableDocumentPanel
            tableUuid={sidePanelState.tableUuid}
            onClose={handleClose}
          />
        );
      case 'saveTableTemplate':
        return (
          <SaveTableTemplatePanel
            tableUuid={sidePanelState.tableUuid}
            defaultName={sidePanelState.defaultName}
            onClose={handleClose}
          />
        );
      case 'manageResourceCollaborators':
        return (
          <ResourceCollaboratorsSidePanel
            resourceUuid={sidePanelState.resourceUuid}
            resourceType={sidePanelState.resourceType}
            collaborators={sidePanelState.collaborators}
            currentUserUuid={sidePanelState.currentUserUuid}
            onUpdate={sidePanelState.onUpdate}
            canAdmin={sidePanelState.canAdmin}
            addCollaborator={sidePanelState.addCollaborator}
            removeCollaborator={sidePanelState.removeCollaborator}
            updateAccess={sidePanelState.updateAccess}
            title={sidePanelState.title}
            subMessage={sidePanelState.subMessage}
            icon={sidePanelState.icon}
            supportsLead={sidePanelState.supportsLead}
            isLead={sidePanelState.isLead}
            setLead={sidePanelState.setLead}
            ownerLabel={sidePanelState.ownerLabel}
          />
        );
      case 'editClauseRule':
        return (
          <ClauseRuleFormPanel
            ref={contentRef}
            reviewUuid={sidePanelState.reviewUuid}
            rule={sidePanelState.rule}
            onClose={handleClose}
          />
        );
      case 'editGlobalRule':
        return (
          <GlobalRuleFormPanel
            ref={contentRef}
            reviewUuid={sidePanelState.reviewUuid}
            rule={sidePanelState.rule}
            onClose={handleClose}
          />
        );
      case 'custom':
      default:
        return null;
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[299] transition-opacity duration-300 ease-in-out ${sidePanelState.showSidePanel ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
        style={{ background: 'rgba(6,6,9,0.56)' }}
        onClick={handleClose}
      ></div>

      <div
        className={`fixed top-0 right-0 h-full z-[300] flex flex-col overflow-hidden bg-white dark:bg-gray-800 transform transition-transform duration-300 ease-in-out ${sidePanelState.showSidePanel ? 'translate-x-0' : 'translate-x-full'
          }`}
        style={{ width: sidePanelState.width }}
      >
        <div className={`flex flex-row justify-between items-center py-4 ${sidePanelState.mb === false && 'mb-6'} border-b border-1 border-gray-300 dark:border-gray-700 px-4`}>
          <div className='flex-1 min-w-0'>
            <div className='flex flex-row items-center gap-2'>
              {sidePanelState.icon && (
                <>
                  {sidePanelState.icon}
                </>
              )}
              {sidePanelState.message && (
                <h3 className='text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 text-left truncate min-w-0 flex-1'>{sidePanelState.message}</h3>
              )}
            </div>
            {sidePanelState.subMsg && (
              <p className='text-sm mt-4 max-w-md text-gray-500 dark:text-gray-300'>{sidePanelState.subMsg}</p>
            )}
          </div>
          <Button
            variant='outline'
            onClick={handleClose}
            size='small'
          >
            {t('Close')}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto relative flex flex-col custom-scrollbar-main bg-[#F5F5F5] dark:bg-gray-800" >
          {renderContent()}
        </div>
        {sidePanelState.actions && sidePanelState.actions.length > 0 && (
          <div className="border-t border-gray-300 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
            {sidePanelState.actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant}
                size={action.size || 'small'}
                onClick={() => action.onClick(contentRef.current)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default GlobalSidePanel;
